import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWebSocket } from '../context/WebSocketContext';

/**
 * Listens for `commission` WebSocket events and surfaces a celebratory toast
 * with a CTA to /referrals. Designed to be mounted once inside the dashboard
 * layouts so the toast fires regardless of which member page is open.
 *
 * Each toast is keyed by amount+timestamp to avoid duplicate firing when the
 * `lastMessage` reference is re-read (e.g. after a re-render).
 */
export function useCommissionToasts() {
  const { lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const lastSeenRef = useRef(null);

  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'commission') return;
    // Dedupe by reference — `lastMessage` is replaced on each new event.
    if (lastSeenRef.current === lastMessage) return;
    lastSeenRef.current = lastMessage;

    const d = lastMessage.data || {};
    const amount = Number(d.amount || 0);
    if (amount <= 0) return;

    const formatted = `PHP ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const who = d.from_name ? ` from ${d.from_name}` : '';
    const levelLabel = d.level_label || (d.level ? `L${d.level} referral` : 'referral');
    const subtitle = `${formatted}${who} (${levelLabel}${d.rate ? ` · ${d.rate}%` : ''})`;

    toast.success('🎉 Commission Earned!', {
      description: subtitle,
      duration: 10000,
      action: {
        label: 'View',
        onClick: () => navigate('/referrals'),
      },
      // data-testid hook lives on the toast viewport; the action button gets the testid
      // for the platform-level testing agent to validate the CTA.
      actionButtonStyle: { backgroundColor: '#00E5FF', color: '#001627', fontWeight: 600 },
    });
  }, [lastMessage, navigate]);
}

export default useCommissionToasts;
